import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useAddMCPServer } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { APPROVAL_TO_VALUE, HumanApprovalField, type HumanApprovalOption } from './human-approval';

type AddMcpServerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectKey: string;
};

type AddMcpServerFormData = {
  toolName: string;
  description: string;
  visibility: ResourceVisibility;
  isOpenTool: boolean;
  isBlocksAuthenticated: boolean;
  humanApproval: HumanApprovalOption;
};

export function AddMcpServerModal({
  open,
  onOpenChange,
  projectKey,
}: AddMcpServerModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY;
  const { mutateAsync: addMCPServer, isPending } = useAddMCPServer();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
    watch,
  } = useForm<AddMcpServerFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      isOpenTool: false,
      isBlocksAuthenticated: false,
      humanApproval: 'auto',
    },
  });

  const isBlocksToolChecked = watch('isOpenTool');

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
    }
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: AddMcpServerFormData) => {
    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: t('MISSING_PROJECT_KEY') || 'Missing project key',
        description:
          t('UNABLE_TO_CREATE_MCP_SERVER') || 'Unable to create the MCP server for this tenant.',
      });
      return;
    }

    try {
      const response = await addMCPServer({
        name: data.toolName.trim(),
        description: data.description.trim(),
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: isAdmin ? data.isOpenTool : false,
        is_blocks_authenticated: isAdmin ? data.isBlocksAuthenticated : false,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
      });

      if (!response.is_success) {
        throw new Error(
          response.message ||
            response.detail ||
            t('FAILED_TO_CREATE_MCP_SERVER') ||
            'Failed to create MCP server.'
        );
      }

      toast({
        variant: 'success',
        title: t('MCP_SERVER_CREATED') || 'MCP server created',
        description:
          response.detail ||
          t('MCP_SERVER_CREATED_SUCCESSFULLY') ||
          'MCP server created successfully.',
      });
      await queryClient.invalidateQueries({ queryKey: ['tools'] });
      handleClose(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('CREATE_FAILED') || 'Create failed',
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_CREATE_MCP_SERVER') || 'Failed to create MCP server.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[640px] rounded-[0.5rem] border-0 p-4 shadow-xl">
        <DialogHeader className="space-y-1 px-0 py-0">
          <DialogTitle className="text-lg font-medium leading-none">
            {t('ADD_MCP_SERVER') || 'Add MCP server'}
          </DialogTitle>
          <DialogDescription className="text-sm leading-6">
            {t('EDIT_MCP_SERVER_DESCRIPTION') ||
              'Define how this MCP server will assist your agent in performing tasks.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-0 py-4">
          <div className="space-y-2">
            <label htmlFor="toolName" className="text-sm font-medium leading-6">
              {t('TOOL_NAME') || 'Tool Name'} <span className="text-destructive">*</span>
            </label>
            <Input
              id="toolName"
              placeholder={t('ENTER_NAME') || 'Enter name'}
              {...register('toolName', {
                required: t('TOOL_NAME_REQUIRED') || 'Tool name is required',
                setValueAs: (value) => value.trim(),
              })}
              className={getToolFormInputClassName(!!errors.toolName)}
            />
            {errors.toolName && (
              <p className="text-sm leading-6 text-destructive">{errors.toolName.message}</p>
            )}
          </div>

          <Controller
            name="visibility"
            control={control}
            render={({ field }) => (
              <VisibilityField
                value={field.value}
                onValueChange={field.onChange}
                idPrefix="add-mcp-server-visibility"
                disabled={isPending}
              />
            )}
          />

          <div className="space-y-2">
            <label htmlFor="description" className="text-sm font-medium leading-6">
              {t('DESCRIPTION') || 'Description'} <span className="text-destructive">*</span>
            </label>
            <Textarea
              id="description"
              rows={4}
              placeholder={
                t('TOOL_DESCRIPTION_PLACEHOLDER') ||
                'Description of this tool for the AI to use in conversations'
              }
              {...register('description', {
                required: t('DESCRIPTION_REQUIRED') || 'Description is required',
                setValueAs: (value) => value.trim(),
              })}
              className={getToolFormTextareaClassName(!!errors.description)}
            />
            {errors.description && (
              <p className="text-sm leading-6 text-destructive">{errors.description.message}</p>
            )}
          </div>

          <HumanApprovalField
            control={control}
            name="humanApproval"
            id="addMcpHumanApproval"
            toolType="mcp"
            disabled={isPending}
          />

          {isAdmin && (
            <div className="space-y-4 pt-1">
              <div className="flex items-center gap-2">
                <Controller
                  name="isOpenTool"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="checkbox"
                      id="isOpenTool"
                      checked={field.value}
                      onChange={field.onChange}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  )}
                />
                <label htmlFor="isOpenTool" className="cursor-pointer text-sm font-medium leading-6">
                  {t('BLOCKS_TOOL') || 'Blocks Tool'}
                </label>
              </div>

              {isBlocksToolChecked && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Controller
                      name="isBlocksAuthenticated"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          id="isBlocksAuthenticated"
                          checked={field.value}
                          onChange={field.onChange}
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary"
                        />
                      )}
                    />
                    <label
                      htmlFor="isBlocksAuthenticated"
                      className="cursor-pointer text-sm font-medium leading-6"
                    >
                      {t('ALLOW_TOOL_AUTHENTICATION') || 'Allow Tool Authentication'}
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger type="button" className="cursor-help">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm leading-6">
                            {t('ALLOW_TOOL_AUTHENTICATION_HELP') ||
                              'If checked, users can copy the tool with its authentication.'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch id="mcpVisibility" checked={false} disabled className="cursor-not-allowed" />
                    <label htmlFor="mcpVisibility" className="text-sm font-medium leading-6">
                      {t('TOOL_VISIBILITY') || 'Tool Visibility'}
                    </label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger type="button" className="cursor-help">
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-sm leading-6">
                            {t('MCP_SERVER_VISIBILITY_HELP') ||
                              'Admin can edit this MCP server later to enable it.'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-0 py-0">
          <Button
            variant="ghost"
            className="h-8 rounded-nav px-3 text-sm text-foreground hover:bg-transparent"
            onClick={() => handleClose(false)}
            disabled={isPending}
          >
            {t('CANCEL') || 'Cancel'}
          </Button>
          <Button
            className="h-8 rounded-nav px-3 text-sm"
            onClick={handleSubmit(onSubmit)}
            disabled={isPending || !isValid}
          >
            {isPending ? t('CREATING') || 'Creating...' : t('CREATE') || 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
